/**
 * List editor for rotating static marketing banner messages.
 */

import React, { useCallback, useMemo } from 'react';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import type { MarketingMessage, MarketingMessageAnimation } from '@/app/types/marketingBanners';
import { MARKETING_MESSAGE_ANIMATION_OPTIONS } from '@/app/types/marketingBanners';
import { Select } from '@/components/common/FormField';

import './StaticMessagesEditor.scss';

interface StaticMessagesEditorProps {
  value: MarketingMessage[];
  onChange: (messages: MarketingMessage[]) => void;
  disabled?: boolean;
}

function nextMessageId(existing: MarketingMessage[]): string {
  const numbers = existing
    .map((message) => {
      const match = /^msg-(\d+)$/.exec(message.id);
      return match ? Number(match[1]) : 0;
    })
    .filter((n) => n > 0);
  const max = numbers.length > 0 ? Math.max(...numbers) : 0;
  return `msg-${max + 1}`;
}

export const StaticMessagesEditor: React.FC<StaticMessagesEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const messages = useMemo(() => value ?? [], [value]);

  const handleAdd = useCallback(() => {
    onChange([...messages, { id: nextMessageId(messages), text: '', animation: 'none' }]);
  }, [messages, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      onChange(messages.filter((_, i) => i !== index));
    },
    [messages, onChange],
  );

  const handleUpdate = useCallback(
    (index: number, html: string) => {
      onChange(messages.map((message, i) => (i === index ? { ...message, text: html } : message)));
    },
    [messages, onChange],
  );

  const handleAnimation = useCallback(
    (index: number, animation: MarketingMessageAnimation) => {
      onChange(messages.map((message, i) => (i === index ? { ...message, animation } : message)));
    },
    [messages, onChange],
  );

  return (
    <div className="static-messages-editor">
      {messages.length === 0 ? (
        <div className="static-messages-editor__empty">
          <p>No rotating messages yet. Add lines of home-banner subtext to rotate.</p>
        </div>
      ) : (
        <div className="static-messages-editor__list">
          {messages.map((message, index) => (
            <div key={message.id} className="static-messages-editor__item">
              <div className="static-messages-editor__item-header">
                <span className="static-messages-editor__item-number">Message {index + 1}</span>
                <button
                  type="button"
                  className="static-messages-editor__remove-btn"
                  onClick={() => handleRemove(index)}
                  disabled={disabled}
                  title="Remove this message"
                >
                  ×
                </button>
              </div>
              <Select
                label="Animation"
                value={message.animation ?? 'none'}
                options={MARKETING_MESSAGE_ANIMATION_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                onChange={(v) => handleAnimation(index, (v || 'none') as MarketingMessageAnimation)}
                disabled={disabled}
              />
              <RichTextEditor
                label={`Message ${index + 1}`}
                value={message.text}
                onChange={(html) => handleUpdate(index, html)}
                placeholder="Enter rotating banner subtext…"
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="static-messages-editor__add-btn"
        onClick={handleAdd}
        disabled={disabled}
      >
        + Add message
      </button>
    </div>
  );
};

export default StaticMessagesEditor;
